// Implementation of the Ford Fulkerson algorithm for finding the maximum flow in a graph
// https://www.geeksforgeeks.org/ford-fulkerson-algorithm-for-maximum-flow-problem/

/** This class abstracts away the in-memory representation of the graph */
export class Graph {
  private graph: number[][] = [];
  private vertexCount: number;

  constructor(size: number) {
    this.graph = new Array(size);
    this.vertexCount = size;
    for (let i = 0; i < size; i++) {
      this.graph[i] = Array(size).fill(0);
    }
  }

  static copy(graph: number[][]) {
    const copy = new Graph(graph.length);
    for (let u = 0; u < graph.length; u++) {
      for (let v = 0; v < graph.length; v++) {
        copy.graph[u][v] = graph[u][v];
      }
    }
    return copy;
  }

  addEdge(from: number, to: number, weight = 1) {
    this.graph[from][to] = weight;
  }

  getEdge(from: number, to: number) {
    return this.graph[from][to];
  }

  build() {
    return this.graph;
  }

  /**
   * Returns the maximum flow from s to t in the graph.
   * In general, the minimum time complexity is O(VE^2).
   * This implementation has a time complexity of O(EV^3) due to the BFS algorithm used.
   *
   * @param source the starting node for the flow
   * @param sink the ending node for the flow
   * @returns the maximum flow possible between the `source` and the `sink`
   */
  fordFulkerson(source: number, sink: number) {
    // Create a residual graph and fill the
    // residual graph with given capacities
    // in the original graph as residual
    // capacities in residual graph

    // Residual graph where rGraph[i][j]
    // indicates residual capacity of edge
    // from i to j (if there is an edge.
    // If rGraph[i][j] is 0, then there is
    // not)
    const residualGraph = Graph.copy(this.graph);

    // There is no flow initially
    let maxFlow = 0;
    let u: number, v: number;

    // Augment the flow while there is a path from source to sink
    let bfs = residualGraph.bfs(source, sink);
    while (bfs.pathFound) {
      // Find minimum residual capacity of the edges
      // along the path filled by BFS. Or we can say
      // find the maximum flow through the path found.
      let pathFlow = Number.MAX_VALUE;
      for (v = sink; v !== source; v = bfs.parent[v]) {
        u = bfs.parent[v];
        pathFlow = Math.min(pathFlow, residualGraph.graph[u][v]);
      }

      // Update residual capacities of the edges and
      // reverse edges along the path
      for (v = sink; v !== source; v = bfs.parent[v]) {
        u = bfs.parent[v];
        residualGraph.graph[u][v] -= pathFlow;
        residualGraph.graph[v][u] += pathFlow;
      }

      // Add path flow to overall flow
      maxFlow += pathFlow;
      // Find a new path
      bfs = residualGraph.bfs(source, sink);
    }

    // Return the overall flow
    return maxFlow;
  }

  /**
   * Returns true if there is a path from source to sink in the residual graph.
   * Also fills parent to store the path.
   * This implementation has a time complexity of O(V^2).
   */
  bfs(source: number, sink: number) {
    // Create a visited array and mark all vertices as not visited
    const visited = new Array<boolean>(this.vertexCount).fill(false);
    const parent = new Array<number>(this.vertexCount);

    // Create a queue, enqueue source vertex
    // and mark source vertex as visited
    const queue: number[] = [];
    queue.push(source);
    visited[source] = true;
    parent[source] = -1;

    // Standard BFS Loop
    while (queue.length !== 0) {
      const u = queue.shift();

      for (let v = 0; v < this.vertexCount; v++) {
        if (!visited[v] && this.graph[u][v] > 0) {
          // If we find a connection to the sink node,
          // then there is no point in BFS anymore
          // We just have to set its parent and can return true
          if (v === sink) {
            parent[v] = u;
            return {
              pathFound: true,
              parent
            };
          }
          queue.push(v);
          parent[v] = u;
          visited[v] = true;
        }
      }
    }

    // We didn't reach sink in BFS starting
    // from source, so return false
    return {
      pathFound: false,
      parent
    };
  }
}
